import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiAlertTriangle, FiBell, FiInfo, FiSearch, FiTag, FiUser, FiX } from 'react-icons/fi';
import BASE_URL from '../../../../config/config';
import { cabecerasAuth } from '../../../../queries/cliente';

export const ICONOS = { campana: FiBell, ticket: FiTag, info: FiInfo, alerta: FiAlertTriangle, usuario: FiUser };
const TIPOS = ['sistema', 'ticket', 'proyecto', 'usuario'];
const PRIORIDADES = ['baja', 'normal', 'alta', 'urgente'];
export const AUDIENCIAS = ['todos', 'clientes', 'admins', 'tecnicos', 'admins_tecnicos', 'especifico'];
const IDIOMAS = ['es', 'en'];
const VACIO = {
  es: { titulo: '', mensaje: '', asunto: '', boton: '' },
  en: { titulo: '', mensaje: '', asunto: '', boton: '' },
};
// Mismos límites que valida el servidor (notificaciones.service.js)
const MAX_ASUNTO = 200;
const MAX_BOTON = 40;
// Espera tras la última tecla antes de pedir el correo renderizado
const ESPERA_PREVIA_MS = 400;

/**
 * Modal para redactar un envío. Guarda su propio estado: al cerrarlo se
 * descarta lo escrito, igual que antes. Formulario a la izquierda y, a la
 * derecha, cómo se verá en la campana y qué falta para poder enviarlo.
 */
const CompositorEnvio = ({ usuarios, onCerrar, onEnviado }) => {
  const { t } = useTranslation();
  const idLista = useId();
  const dialogo = useRef(null);

  const [audiencia, setAudiencia] = useState('todos');
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState('');
  const [busquedaUsuario, setBusquedaUsuario] = useState('');
  const [mostrarUsuarios, setMostrarUsuarios] = useState(false);
  const [resaltado, setResaltado] = useState(0);
  const [contenido, setContenido] = useState(VACIO);
  const [idiomaActivo, setIdiomaActivo] = useState('es');
  const [tipo, setTipo] = useState('sistema');
  const [prioridad, setPrioridad] = useState('normal');
  const [icono, setIcono] = useState('campana');
  const [link, setLink] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [vistaPrevia, setVistaPrevia] = useState('campana');
  const [correoPrevio, setCorreoPrevio] = useState(null);
  const [cargandoCorreo, setCargandoCorreo] = useState(false);

  // El foco entra en el diálogo una sola vez, al abrirlo. Si dependiera de
  // onCerrar (una función nueva en cada render del padre) se lo robaría al
  // campo en el que se está escribiendo.
  useEffect(() => { dialogo.current?.focus(); }, []);

  const cerrarRef = useRef(onCerrar);
  cerrarRef.current = onCerrar;
  useEffect(() => {
    const cerrar = (event) => { if (event.key === 'Escape' && !enviando) cerrarRef.current(); };
    document.addEventListener('keydown', cerrar);
    return () => document.removeEventListener('keydown', cerrar);
  }, [enviando]);

  const actualizarContenido = (campo, valor) => setContenido((actual) => ({
    ...actual, [idiomaActivo]: { ...actual[idiomaActivo], [campo]: valor },
  }));

  const destinatarios = useMemo(() => {
    if (audiencia === 'especifico') return usuarioSeleccionado ? 1 : 0;
    if (audiencia === 'clientes') return usuarios.filter((u) => u.role === 'user').length;
    if (audiencia === 'admins') return usuarios.filter((u) => u.role === 'admin').length;
    if (audiencia === 'tecnicos') return usuarios.filter((u) => u.role === 'tecnico').length;
    if (audiencia === 'admins_tecnicos') return usuarios.filter((u) => u.role === 'admin' || u.role === 'tecnico').length;
    return usuarios.length;
  }, [audiencia, usuarioSeleccionado, usuarios]);

  const usuariosFiltrados = useMemo(() => {
    const texto = busquedaUsuario.trim().toLowerCase();
    if (!texto) return [];
    return usuarios.filter((u) => `${u.nombre} ${u.apellidos} ${u.usuario} ${u.email}`.toLowerCase().includes(texto)).slice(0, 8);
  }, [busquedaUsuario, usuarios]);

  const elegirUsuario = (u) => {
    setUsuarioSeleccionado(u.usuario);
    setBusquedaUsuario(`${u.nombre} ${u.apellidos} (@${u.usuario})`);
    setMostrarUsuarios(false);
  };

  // Flechas y Enter para elegir sin soltar el teclado
  const teclasBuscador = (event) => {
    if (!mostrarUsuarios || !usuariosFiltrados.length) return;
    if (event.key === 'ArrowDown') { event.preventDefault(); setResaltado((i) => (i + 1) % usuariosFiltrados.length); }
    if (event.key === 'ArrowUp') { event.preventDefault(); setResaltado((i) => (i - 1 + usuariosFiltrados.length) % usuariosFiltrados.length); }
    if (event.key === 'Enter') { event.preventDefault(); elegirUsuario(usuariosFiltrados[resaltado]); }
  };

  // Qué le falta a cada idioma: se enseña en la vista previa
  const faltaEn = (idioma) => {
    const { titulo, mensaje } = contenido[idioma];
    if (!titulo.trim() && !mensaje.trim()) return 'todo';
    if (!titulo.trim()) return 'titulo';
    if (!mensaje.trim()) return 'mensaje';
    return null;
  };

  const enviar = async (event) => {
    event.preventDefault();
    if (audiencia === 'especifico' && !usuarioSeleccionado) {
      toast.error(t('gestorNotificaciones.seleccionaUsuarioDestinatario')); return;
    }
    if (IDIOMAS.some((idioma) => faltaEn(idioma))) {
      toast.error(t('gestorNotificaciones.completaIdiomas')); return;
    }
    setEnviando(true);
    try {
      const { data } = await axios.post(`${BASE_URL}/notificaciones`, {
        audiencia,
        usuario_destinatario: audiencia === 'especifico' ? usuarioSeleccionado : null,
        contenido,
        tipo, prioridad, icono, link: link.trim() || null,
      }, cabecerasAuth());
      toast.success(t('gestorNotificaciones.envioEncolado', {
        destinatarios: data.destinatarios,
        correos: data.correosEncolados,
      }));
      onEnviado();
    } catch (error) {
      toast.error(error.response?.data?.message || t('gestorNotificaciones.errorEnviar'));
    } finally { setEnviando(false); }
  };

  const IconoPrevia = ICONOS[icono] || FiBell;
  const actual = contenido[idiomaActivo];

  // El correo lo compone el servidor con la misma plantilla que el envío real:
  // lo que se ve aquí es exactamente lo que llega. Solo se pide con la pestaña
  // "Correo" abierta y un momento después de dejar de escribir.
  useEffect(() => {
    if (vistaPrevia !== 'correo') return undefined;
    let vigente = true;
    setCargandoCorreo(true);
    const temporizador = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${BASE_URL}/notificaciones-admin/previsualizar-correo`, {
          idioma: idiomaActivo,
          titulo: actual.titulo || t('gestorNotificaciones.previewTitulo', { lng: idiomaActivo }),
          mensaje: actual.mensaje || t('gestorNotificaciones.previewMensaje', { lng: idiomaActivo }),
          asunto: actual.asunto,
          boton: actual.boton,
          prioridad,
          link: link.trim() || null,
        }, cabecerasAuth());
        if (vigente) setCorreoPrevio({ asunto: data.asunto, html: data.html });
      } catch {
        if (vigente) setCorreoPrevio(null);
      } finally {
        if (vigente) setCargandoCorreo(false);
      }
    }, ESPERA_PREVIA_MS);
    return () => { vigente = false; clearTimeout(temporizador); };
  }, [vistaPrevia, idiomaActivo, actual.titulo, actual.mensaje, actual.asunto, actual.boton, prioridad, link, t]);

  return (
    <div className="ntf-capa" onMouseDown={(e) => { if (e.target === e.currentTarget && !enviando) onCerrar(); }}>
      <section ref={dialogo} tabIndex={-1} className="ntf-modal" role="dialog" aria-modal="true" aria-labelledby="ntf-modal-titulo">
        <div className="ntf-modal-cabecera">
          <h2 id="ntf-modal-titulo">{t('gestorNotificaciones.nuevoEnvio')}</h2>
          <button type="button" className="ntf-cerrar" onClick={onCerrar} disabled={enviando} aria-label={t('comun.cerrar')}><FiX /></button>
        </div>

        <form className="ntf-modal-form" onSubmit={enviar}>
          <div className="ntf-modal-cuerpo">
            <div className="ntf-formulario">
              <fieldset className="ntf-bloque">
                <legend>{t('gestorNotificaciones.nt.paraQuien')}</legend>
                <div className="ntf-audiencias">
                  {AUDIENCIAS.map((valor) => (
                    <label key={valor} className="ntf-audiencia">
                      <input
                        type="radio"
                        name="ntf-audiencia"
                        value={valor}
                        checked={audiencia === valor}
                        onChange={() => {
                          setAudiencia(valor);
                          if (valor !== 'especifico') { setUsuarioSeleccionado(''); setBusquedaUsuario(''); }
                        }}
                      />
                      <span>{t(`gestorNotificaciones.destinatarios.${valor}`)}</span>
                    </label>
                  ))}
                </div>
                <p className="ntf-ayuda">{t(`gestorNotificaciones.audienciaDesc.${audiencia}`)}</p>

                {audiencia === 'especifico' && (
                  <div className="ntf-campo ntf-buscador-usuario">
                    <label htmlFor="ntf-usuario">{t('gestorNotificaciones.usuarioDestinatario')}</label>
                    <div className="ntf-input-icono">
                      <FiSearch aria-hidden="true" />
                      <input
                        id="ntf-usuario"
                        role="combobox"
                        aria-expanded={mostrarUsuarios && usuariosFiltrados.length > 0}
                        aria-controls={idLista}
                        aria-autocomplete="list"
                        autoComplete="off"
                        value={busquedaUsuario}
                        onChange={(e) => { setBusquedaUsuario(e.target.value); setUsuarioSeleccionado(''); setMostrarUsuarios(true); setResaltado(0); }}
                        onFocus={() => setMostrarUsuarios(true)}
                        onBlur={() => setTimeout(() => setMostrarUsuarios(false), 120)}
                        onKeyDown={teclasBuscador}
                        placeholder={t('gestorNotificaciones.buscarUsuarioPlaceholder')}
                      />
                    </div>
                    {mostrarUsuarios && busquedaUsuario.trim() && !usuarioSeleccionado && (
                      usuariosFiltrados.length > 0 ? (
                        <ul className="ntf-opciones-usuario" id={idLista} role="listbox">
                          {usuariosFiltrados.map((u, i) => (
                            <li
                              key={u.usuario}
                              role="option"
                              aria-selected={i === resaltado}
                              className={i === resaltado ? 'resaltado' : undefined}
                              onMouseDown={(e) => { e.preventDefault(); elegirUsuario(u); }}
                              onMouseEnter={() => setResaltado(i)}
                            >
                              <span>
                                <strong>{u.nombre} {u.apellidos}</strong>
                                <small>@{u.usuario}, {u.email}</small>
                              </span>
                              <em>{String(u.idioma || 'es').toUpperCase()}</em>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="ntf-ayuda">{t('gestorNotificaciones.sinUsuariosEncontrados')}</p>
                      )
                    )}
                  </div>
                )}
              </fieldset>

              <fieldset className="ntf-bloque">
                <legend>{t('gestorNotificaciones.nt.mensaje')}</legend>
                <p className="ntf-ayuda ntf-ayuda--arriba">{t('gestorNotificaciones.contenidoAyuda')}</p>
                <div className="ntf-idiomas" role="tablist" aria-label={t('gestorNotificaciones.nt.idiomaDelTexto')}>
                  {IDIOMAS.map((idioma) => (
                    <button
                      type="button"
                      role="tab"
                      key={idioma}
                      aria-selected={idiomaActivo === idioma}
                      className={idiomaActivo === idioma ? 'activo' : undefined}
                      onClick={() => setIdiomaActivo(idioma)}
                    >
                      {t(`gestorNotificaciones.idiomas.${idioma}`)}
                      <i className={faltaEn(idioma) ? 'pendiente' : 'listo'} aria-hidden="true" />
                    </button>
                  ))}
                </div>
                <div className="ntf-campo">
                  <label htmlFor="ntf-titulo">{t('gestorNotificaciones.nt.titulo')}</label>
                  <input id="ntf-titulo" maxLength={255} value={actual.titulo} onChange={(e) => actualizarContenido('titulo', e.target.value)} />
                </div>
                <div className="ntf-campo">
                  <label htmlFor="ntf-mensaje">{t('gestorNotificaciones.nt.texto')}</label>
                  <textarea id="ntf-mensaje" rows={5} value={actual.mensaje} onChange={(e) => actualizarContenido('mensaje', e.target.value)} />
                </div>

                <div className="ntf-subbloque">
                  <h4>{t('gestorNotificaciones.nt.enElCorreo')}</h4>
                  <div className="ntf-campo">
                    <label htmlFor="ntf-asunto">{t('gestorNotificaciones.nt.asunto')}</label>
                    <input
                      id="ntf-asunto"
                      maxLength={MAX_ASUNTO}
                      value={actual.asunto}
                      onChange={(e) => actualizarContenido('asunto', e.target.value)}
                      placeholder={t('gestorNotificaciones.nt.asuntoPorDefecto', { lng: idiomaActivo, titulo: actual.titulo || '…' })}
                      aria-describedby="ntf-asunto-ayuda"
                    />
                    <p className="ntf-ayuda" id="ntf-asunto-ayuda">{t('gestorNotificaciones.nt.asuntoAyuda')}</p>
                  </div>
                  <div className="ntf-campo">
                    <label htmlFor="ntf-boton">{t('gestorNotificaciones.nt.boton')}</label>
                    <input
                      id="ntf-boton"
                      maxLength={MAX_BOTON}
                      value={actual.boton}
                      onChange={(e) => actualizarContenido('boton', e.target.value)}
                      placeholder={t('gestorNotificaciones.nt.botonPorDefecto', { lng: idiomaActivo })}
                      aria-describedby="ntf-boton-ayuda"
                    />
                    <p className="ntf-ayuda" id="ntf-boton-ayuda">
                      {link.trim() ? t('gestorNotificaciones.nt.botonAyudaEnlace') : t('gestorNotificaciones.nt.botonAyudaPanel')}
                    </p>
                  </div>
                </div>
              </fieldset>

              <fieldset className="ntf-bloque">
                <legend>{t('gestorNotificaciones.nt.opciones')}</legend>
                <div className="ntf-trio">
                  <div className="ntf-campo">
                    <label htmlFor="ntf-tipo">{t('gestorNotificaciones.nt.tipo')}</label>
                    <select id="ntf-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                      {TIPOS.map((v) => <option value={v} key={v}>{t(`gestorNotificaciones.tipos.${v}`)}</option>)}
                    </select>
                  </div>
                  <div className="ntf-campo">
                    <label htmlFor="ntf-prioridad">{t('gestorNotificaciones.prioridad')}</label>
                    <select id="ntf-prioridad" value={prioridad} onChange={(e) => setPrioridad(e.target.value)}>
                      {PRIORIDADES.map((v) => <option value={v} key={v}>{t(`gestorNotificaciones.prioridades.${v}`)}</option>)}
                    </select>
                  </div>
                  <div className="ntf-campo">
                    <label htmlFor="ntf-icono">{t('gestorNotificaciones.icono')}</label>
                    <select id="ntf-icono" value={icono} onChange={(e) => setIcono(e.target.value)}>
                      {Object.keys(ICONOS).map((v) => <option value={v} key={v}>{t(`gestorNotificaciones.iconos.${v}`)}</option>)}
                    </select>
                  </div>
                </div>
                <div className="ntf-campo">
                  <label htmlFor="ntf-link">{t('gestorNotificaciones.enlaceOpcional')}</label>
                  <input id="ntf-link" value={link} onChange={(e) => setLink(e.target.value)} placeholder={t('gestorNotificaciones.placeholderEnlace')} aria-describedby="ntf-link-ayuda" />
                  <p className="ntf-ayuda" id="ntf-link-ayuda">{t('gestorNotificaciones.hintEnlace')}</p>
                </div>
              </fieldset>
            </div>

            <aside className="ntf-previa" aria-label={t('gestorNotificaciones.vistaPrevia')}>
              <div className="ntf-previa-pestanas" role="tablist" aria-label={t('gestorNotificaciones.vistaPrevia')}>
                {['campana', 'correo'].map((vista) => (
                  <button
                    key={vista}
                    type="button"
                    role="tab"
                    aria-selected={vistaPrevia === vista}
                    className={vistaPrevia === vista ? 'activa' : undefined}
                    onClick={() => setVistaPrevia(vista)}
                  >
                    {t(`gestorNotificaciones.nt.previa_${vista}`)}
                  </button>
                ))}
              </div>

              {vistaPrevia === 'campana' ? (
                <div className={`ntf-previa-aviso prioridad-${prioridad}`}>
                  <span className="ntf-previa-icono"><IconoPrevia aria-hidden="true" /></span>
                  <div>
                    <strong className={actual.titulo ? undefined : 'vacio'}>{actual.titulo || t('gestorNotificaciones.previewTitulo')}</strong>
                    <p className={actual.mensaje ? undefined : 'vacio'}>{actual.mensaje || t('gestorNotificaciones.previewMensaje')}</p>
                    <small>{t('gestorNotificaciones.ahora')}</small>
                  </div>
                </div>
              ) : (
                <div className={`ntf-previa-correo${cargandoCorreo ? ' cargando' : ''}`} aria-busy={cargandoCorreo}>
                  <div className="ntf-previa-asunto">
                    <span>{t('gestorNotificaciones.nt.asunto')}</span>
                    <strong>{correoPrevio?.asunto || '…'}</strong>
                  </div>
                  {correoPrevio ? (
                    <iframe
                      title={t('gestorNotificaciones.nt.previa_correo')}
                      srcDoc={correoPrevio.html}
                      sandbox=""
                    />
                  ) : (
                    <p className="ntf-ayuda">{cargandoCorreo ? t('comun.cargando') : t('gestorNotificaciones.nt.previaError')}</p>
                  )}
                </div>
              )}

              <dl className="ntf-previa-datos">
                <div>
                  <dt>{t('gestorNotificaciones.nt.llegaA')}</dt>
                  <dd>{t('gestorNotificaciones.nt.personas', { count: destinatarios })}</dd>
                </div>
                {IDIOMAS.map((idioma) => {
                  const falta = faltaEn(idioma);
                  return (
                    <div key={idioma}>
                      <dt>{t(`gestorNotificaciones.idiomas.${idioma}`)}</dt>
                      <dd className={falta ? 'pendiente' : 'listo'}>
                        {falta ? t(`gestorNotificaciones.nt.falta_${falta}`) : t('gestorNotificaciones.nt.listo')}
                      </dd>
                    </div>
                  );
                })}
              </dl>
              <p className="ntf-ayuda">{t('gestorNotificaciones.envioIdiomaNota')}</p>
            </aside>
          </div>

          <div className="ntf-modal-pie">
            <button type="button" className="ntf-boton-secundario" onClick={onCerrar} disabled={enviando}>{t('comun.cancelar')}</button>
            <button type="submit" className="ntf-boton" disabled={enviando || destinatarios === 0}>
              {enviando ? t('gestorNotificaciones.enviando') : t('gestorNotificaciones.nt.enviarA', { count: destinatarios })}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

export default CompositorEnvio;
